/**
 * ActionLog — superadmin panel over the platform AUDIT LOG (`actionLog`):
 * every mutating action with who it is CREDITED to (`updatedBy`) and who
 * actually PERFORMED it (`actionPerformedBy`). The two differ only on
 * attributed superadmin edits — those rows are flagged as admin interventions.
 * Filters by action / user text / interventions-only, with running totals.
 * Read-only (rules: superadmin read).
 */
import { useMemo, useState } from 'react';
import type { ActionLogEntry } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';

/** True when the row is an admin intervention (performer ≠ credited user). */
const isIntervention = (r: ActionLogEntry): boolean => r.actionPerformedBy !== r.updatedBy;

/** Compact `k=v` rendering of the details map. */
const detailsText = (details?: Record<string, string | number>): string => {
  const entries = Object.entries(details ?? {});
  return entries.length === 0 ? '—' : entries.map(([k, v]) => `${k}=${v}`).join(' · ');
};

export function ActionLog() {
  const services = useServices();
  const [error, setError] = useState<string | null>(null);
  const rows = useSubscription<Array<ActionLogEntry & { id: string }>>(
    (cb, onError) =>
      services.subscribe.actionLog((next) => {
        setError(null);
        cb(next);
      }, onError),
    [],
    [],
    {
      // actionLog is superadmin-read; a denied listener degrades visibly.
      onError: (err) =>
        setError(err instanceof Error ? err.message : 'Action log is not readable.'),
    },
  );

  const [action, setAction] = useState('');
  const [userQ, setUserQ] = useState('');
  const [interventionsOnly, setInterventionsOnly] = useState(false);

  const actions = useMemo(() => Array.from(new Set(rows.map((r) => r.action))).sort(), [rows]);

  const needle = userQ.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      (!action || r.action === action) &&
      (!needle ||
        [r.updatedByHandle, r.actionPerformedByHandle, r.updatedBy, r.actionPerformedBy].some(
          (s) => (s || '').toLowerCase().includes(needle),
        )) &&
      (!interventionsOnly || isIntervention(r)),
  );
  const interventionCount = filtered.filter(isIntervention).length;

  return (
    <div className="panel" data-testid="action-log">
      <h2 className="panel__title">Action log — audit</h2>
      <p className="gallery__subtitle">
        Every platform mutation: who it’s credited to and who performed it (newest 300).
      </p>
      {error && (
        <p className="muted" style={{ color: 'var(--danger, #f87171)' }}>
          Could not load the action log: {error}
        </p>
      )}

      <div className="row" style={{ gap: 12, flexWrap: 'wrap', margin: '10px 0' }}>
        <select className="select" value={action} onChange={(e) => setAction(e.target.value)} style={{ width: 'auto' }}>
          <option value="">all actions</option>
          {actions.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <input
          className="input"
          style={{ width: 220 }}
          value={userQ}
          onChange={(e) => setUserQ(e.target.value)}
          placeholder="filter by user…"
          aria-label="Filter by user"
        />
        <label className="row" style={{ gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={interventionsOnly}
            onChange={(e) => setInterventionsOnly(e.target.checked)}
          />
          admin interventions only
        </label>
      </div>

      <div className="row" style={{ gap: 22, margin: '6px 0 14px' }} data-testid="action-log-totals">
        <span><strong>{filtered.length}</strong> actions</span>
        <span><strong>{interventionCount}</strong> admin interventions</span>
      </div>

      {filtered.length === 0 ? (
        <p className="muted">No actions logged yet.</p>
      ) : (
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Game</th>
                <th>Updated by</th>
                <th>Performed by</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid="action-log-row">
                  <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                  <td>{r.action}</td>
                  <td>{r.title || r.slug || (r.creationId ? `${r.creationId.slice(0, 10)}…` : '—')}</td>
                  <td>@{r.updatedByHandle || r.updatedBy}</td>
                  <td>
                    {isIntervention(r) ? (
                      <span data-testid="intervention">
                        <strong>@{r.actionPerformedByHandle || r.actionPerformedBy}</strong>{' '}
                        <span className="pill">admin</span>
                      </span>
                    ) : (
                      <>@{r.actionPerformedByHandle || r.actionPerformedBy}</>
                    )}
                  </td>
                  <td className="muted">{detailsText(r.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
